/**
 * Facturas proforma.
 *
 * Existen para un caso muy concreto: el técnico termina la asistencia, el
 * cliente quiere pagar allí mismo y la factura todavía no está hecha. La
 * proforma tiene importe y **no es fiscal** — se ve en su numeración,
 * `PRO-2026-00001`, distinta de la serie de facturas a propósito.
 *
 * Lo que esta pantalla contesta de un vistazo es la pregunta de
 * administración: **qué hay cobrado y sin facturar**. Mientras una proforma
 * esté en esa lista, hay dinero entrado y trabajo por facturar.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { money, day } from '../table/format.js';

interface Proforma {
  id: string;
  number: string;
  issued_on: string;
  status: string;
  total_cents: string;
  customer_name: string;
  cobrado: string;
}

interface Cliente {
  id: string;
  legal_name: string;
}

interface Linea {
  description: string;
  quantity: string;
  unitPrice: string;
}

const ESTADOS: Record<string, { etiqueta: string; tono: string }> = {
  EMITIDA: { etiqueta: 'Emitida', tono: 'pill--warn' },
  COBRADA: { etiqueta: 'Cobrada', tono: 'pill--ok' },
  FACTURADA: { etiqueta: 'Facturada', tono: 'pill--off' },
  ANULADA: { etiqueta: 'Anulada', tono: 'pill--bad' },
};

const METODOS = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'BIZUM', label: 'Bizum' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
];

/** Los céntimos que guarda la base, en euros para leer. */
function euros(centimos: string): string {
  return money((Number(centimos) / 100).toFixed(2));
}

const LINEA_VACIA: Linea = { description: '', quantity: '1', unitPrice: '' };

export function ProformasScreen(): React.ReactNode {
  const { api, user } = useSession();
  const puedeCrear = hasPermission(user, 'sales.create');
  const puedeCobrar = hasPermission(user, 'treasury.create');

  const [items, setItems] = useState<Proforma[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [alta, setAlta] = useState<{ customerId: string; lines: Linea[] } | null>(null);
  const [cobrando, setCobrando] = useState<{ id: string; method: string; amount: string } | null>(
    null,
  );
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const datos = await api.get<{ items: Proforma[] }>('/proformas');
      setItems(datos.items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar.');
    }
  }, [api]);

  useEffect(() => {
    void cargar();
    void api
      .get<{ items: Cliente[] }>('/customers?limit=200')
      .then((d) => setClientes(d.items))
      .catch(() => undefined);
  }, [api, cargar]);

  async function crear(): Promise<void> {
    if (alta === null) return;
    setGuardando(true);
    setError(null);
    try {
      const lineas = alta.lines.filter((l) => l.description.trim() !== '' && l.unitPrice !== '');
      const r = await api.post<{ number: string }>('/proformas', {
        customerId: alta.customerId,
        lines: lineas.map((l) => ({
          description: l.description.trim(),
          quantity: l.quantity === '' ? '1' : l.quantity,
          unitPrice: l.unitPrice,
        })),
      });
      setAviso(`Proforma ${r.number} emitida.`);
      setAlta(null);
      await cargar();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido emitir.');
    } finally {
      setGuardando(false);
    }
  }

  async function cobrar(): Promise<void> {
    if (cobrando === null) return;
    setGuardando(true);
    setError(null);
    try {
      await api.post(`/proformas/${cobrando.id}/payments`, {
        method: cobrando.method,
        amount: cobrando.amount,
      });
      setAviso('Cobro registrado.');
      setCobrando(null);
      await cargar();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido registrar el cobro.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1>Proformas</h1>
        <p className="screen__note">
          Documento con importe y <strong>sin valor fiscal</strong>, para cobrar cuando la factura
          todavía no existe. Lo que se ve aquí es lo cobrado y sin facturar.
        </p>
        {puedeCrear && alta === null && (
          <button
            type="button"
            className="button button--primary button--small"
            onClick={() => setAlta({ customerId: '', lines: [{ ...LINEA_VACIA }] })}
          >
            Nueva proforma
          </button>
        )}
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {aviso !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {aviso}
        </p>
      )}

      {alta !== null && (
        <div className="card form">
          <div className="form__grid">
            <label className="field">
              Cliente
              <select
                value={alta.customerId}
                onChange={(e) => setAlta({ ...alta, customerId: e.target.value })}
              >
                <option value="">Elija cliente</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.legal_name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {alta.lines.map((l, i) => (
            <div key={i} className="line-form__row">
              <label className="field line-form__grow">
                Concepto
                <input
                  value={l.description}
                  onChange={(e) => {
                    const lines = [...alta.lines];
                    lines[i] = { ...l, description: e.target.value };
                    setAlta({ ...alta, lines });
                  }}
                />
              </label>
              <label className="field">
                Cantidad
                <input
                  value={l.quantity}
                  onChange={(e) => {
                    const lines = [...alta.lines];
                    lines[i] = { ...l, quantity: e.target.value };
                    setAlta({ ...alta, lines });
                  }}
                />
              </label>
              <label className="field">
                Precio
                <input
                  value={l.unitPrice}
                  onChange={(e) => {
                    const lines = [...alta.lines];
                    lines[i] = { ...l, unitPrice: e.target.value };
                    setAlta({ ...alta, lines });
                  }}
                  placeholder="30.00"
                />
              </label>
            </div>
          ))}

          <div className="lines__actions">
            <button
              type="button"
              className="button button--quiet button--small"
              onClick={() => setAlta({ ...alta, lines: [...alta.lines, { ...LINEA_VACIA }] })}
            >
              Añadir concepto
            </button>
          </div>

          <p className="screen__note">
            El IVA se aplica al 21 % sobre cada concepto. La proforma no se declara: cuando se
            emita la factura, este cobro se aplica a ella.
          </p>

          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void crear()}
              disabled={
                guardando ||
                alta.customerId === '' ||
                !alta.lines.some((l) => l.description.trim() !== '' && l.unitPrice !== '')
              }
            >
              {guardando ? 'Emitiendo…' : 'Emitir proforma'}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setAlta(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {items.length === 0 && (
        <p className="screen__note">No hay proformas pendientes de facturar.</p>
      )}

      {items.map((p) => {
        const estado = ESTADOS[p.status] ?? { etiqueta: p.status, tono: '' };
        const pendiente = Number(p.total_cents) / 100 - Number(p.cobrado);
        return (
          <div key={p.id} className="card">
            <div className="card__label">
              {p.number} <span className={`pill ${estado.tono}`}>{estado.etiqueta}</span>
            </div>
            <p className="card__meta">
              {p.customer_name} · {day(p.issued_on)} · total {euros(p.total_cents)} · cobrado{' '}
              {money(p.cobrado)}
              {pendiente > 0.004 && ` · quedan ${money(pendiente.toFixed(2))}`}
            </p>

            {puedeCobrar && p.status !== 'ANULADA' && pendiente > 0.004 && (
              cobrando?.id === p.id ? (
                <div className="radar__fields">
                  <label className="field">
                    Forma de cobro
                    <select
                      value={cobrando.method}
                      onChange={(e) => setCobrando({ ...cobrando, method: e.target.value })}
                    >
                      {METODOS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    Importe
                    <input
                      value={cobrando.amount}
                      onChange={(e) => setCobrando({ ...cobrando, amount: e.target.value })}
                    />
                  </label>
                  <button
                    type="button"
                    className="button button--primary button--small"
                    onClick={() => void cobrar()}
                    disabled={guardando || cobrando.amount === ''}
                  >
                    Registrar cobro
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--small"
                    onClick={() => setCobrando(null)}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="button button--quiet button--small"
                  onClick={() =>
                    setCobrando({
                      id: p.id,
                      method: 'EFECTIVO',
                      // Se propone lo que falta, que es lo que se cobra casi siempre.
                      amount: pendiente.toFixed(2),
                    })
                  }
                >
                  Registrar cobro
                </button>
              )
            )}
          </div>
        );
      })}
    </section>
  );
}
